import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOT_OPTIONS = ["Any Available Lot", "Lot S1", "Lot S3A1", "Lot S4A", "Lot S5A", "Lot S6A", "Lot S7A", "Lot S8A"];
const TIMES = ["Morning", "Afternoon", "Evening", "Flexible"];

const inputCls =
  "bg-white border-forest/20 text-forest placeholder:text-forest/40 rounded-sm focus-visible:ring-1 focus-visible:ring-brass focus-visible:ring-offset-0 h-12";
const labelCls = "text-forest/80 font-sans text-xs tracking-[0.15em] uppercase";
const itemCls = "focus:bg-forest/10 focus:text-forest";

const EMPTY = { name: "", phone: "", email: "", lot: "Any Available Lot", date: "", time: "Flexible" };

export const BookingDialog = ({ open, onOpenChange }) => {
  const [f, setF] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const today = new Date().toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    const [first, ...rest] = f.name.trim().split(/\s+/);
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        first_name: first,
        last_name: rest.join(" ") || "—",
        email: f.email,
        phone: f.phone,
        lot_of_interest: f.lot,
        preferred_contact: "Phone",
        inquiry_type: "Showing Request",
        preferred_date: f.date,
        preferred_time: f.time,
        message: `Showing request for ${f.lot} — preferred ${f.date} (${f.time}).`,
      });
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please call 504-329-6166 to book your showing.");
    } finally {
      setSending(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(false); setF(EMPTY); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="booking-dialog" className="max-w-lg bg-cream border-brass/40 rounded-sm p-0 overflow-hidden">
        <div className="bg-forest px-8 py-6">
          <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-brass font-semibold mb-2">Woodlake Estates</p>
          <DialogTitle className="font-serif text-3xl text-cream font-light">Book a Private Showing</DialogTitle>
          <DialogDescription className="font-sans text-sm text-cream/70 mt-2">
            Tell us when works for you and we will confirm your visit within one business day.
          </DialogDescription>
        </div>

        {done ? (
          <div data-testid="booking-confirmation" className="px-8 py-14 text-center flex flex-col items-center gap-4">
            <CheckCircle2 size={44} className="text-brass" />
            <h3 className="font-serif text-3xl text-forest">You're on the calendar</h3>
            <p className="font-sans text-sm text-forest/70 max-w-sm">
              We received your showing request for {f.date}. We will call {f.phone} to confirm the time.
            </p>
            <button data-testid="booking-close-btn" onClick={() => close(false)} className="mt-4 bg-forest text-cream font-sans text-sm font-semibold px-8 py-3 rounded-sm hover:bg-[#1f3d31] transition-colors duration-300">
              Done
            </button>
          </div>
        ) : (
          <form data-testid="booking-form" onSubmit={submit} className="px-8 py-8 grid sm:grid-cols-2 gap-5">
            <div className="flex flex-col gap-2 sm:col-span-2">
              <Label htmlFor="bk-name" className={labelCls}>Full Name</Label>
              <Input id="bk-name" data-testid="booking-name-input" required value={f.name} onChange={set("name")} className={inputCls} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="bk-phone" className={labelCls}>Phone</Label>
              <Input id="bk-phone" data-testid="booking-phone-input" type="tel" required value={f.phone} onChange={set("phone")} className={inputCls} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="bk-email" className={labelCls}>Email</Label>
              <Input id="bk-email" data-testid="booking-email-input" type="email" required value={f.email} onChange={set("email")} className={inputCls} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="bk-date" className={labelCls}>Preferred Date</Label>
              <Input id="bk-date" data-testid="booking-date-input" type="date" min={today} required value={f.date} onChange={set("date")} className={inputCls} />
            </div>
            <div className="flex flex-col gap-2">
              <Label className={labelCls}>Preferred Time</Label>
              <Select value={f.time} onValueChange={(v) => setF((s) => ({ ...s, time: v }))}>
                <SelectTrigger data-testid="booking-time-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-cream border-forest/20 text-forest rounded-sm">
                  {TIMES.map((t) => <SelectItem key={t} value={t} data-testid={`booking-time-${t.toLowerCase()}`} className={itemCls}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2 sm:col-span-2">
              <Label className={labelCls}>Lot to Visit</Label>
              <Select value={f.lot} onValueChange={(v) => setF((s) => ({ ...s, lot: v }))}>
                <SelectTrigger data-testid="booking-lot-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-cream border-forest/20 text-forest rounded-sm">
                  {LOT_OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`booking-lot-${o.toLowerCase().replace(/\s+/g, "-")}`} className={itemCls}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <button
              data-testid="booking-submit-btn"
              type="submit"
              disabled={sending}
              className="sm:col-span-2 mt-2 flex items-center justify-center gap-3 bg-brass text-forest font-sans text-sm font-semibold tracking-[0.15em] uppercase px-8 py-4 rounded-sm transition-colors duration-300 hover:bg-[#cbaa66] disabled:opacity-60"
            >
              <CalendarCheck size={18} /> {sending ? "Booking…" : "Book My Showing"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
